/**
 * MIY Fresh — media sections.
 *
 * Two jobs, no library, no globals: the video facade and the carousel arrows.
 *
 * WHY A FACADE
 * A YouTube iframe costs upwards of 500 KB and several third-party connections
 * before a shopper has expressed any interest in watching. This store's field
 * LCP is already Moderate at 2936 ms. The click path fetches nothing until the
 * poster button is pressed — the poster is an ordinary lazy image that costs
 * what any other photograph costs. The loop path (an uploaded first-party
 * video, or a bundled theme asset) defers its own bytes until the section
 * actually scrolls into view, per the IntersectionObserver below, and is
 * skipped entirely for a shopper who asks for reduced motion.
 */
(function () {
	'use strict';

	function playerFor(kind, id) {
		if (kind === 'file') {
			var v = document.createElement('video');
			v.src = id;
			v.controls = true;
			v.autoplay = true;
			v.playsInline = true;
			v.className = 'miy-videosec__native';
			return v;
		}

		var src =
			kind === 'vimeo'
				? 'https://player.vimeo.com/video/' + id + '?autoplay=1&portrait=0&byline=0'
				: 'https://www.youtube.com/embed/' + id + '?autoplay=1&rel=0&modestbranding=1';

		var f = document.createElement('iframe');
		f.src = src;
		f.title = 'Video';
		f.allow = 'autoplay; fullscreen; picture-in-picture';
		f.allowFullscreen = true;
		f.className = 'miy-videosec__embed';
		return f;
	}

	document.addEventListener('click', function (event) {
		var button = event.target.closest('[data-miy-video="click"]');
		if (!button) return;

		var kind = button.getAttribute('data-miy-video-kind');
		var id = button.getAttribute('data-miy-video-id');
		if (!kind || !id) return;

		var frame = document.createElement('div');
		frame.className = button.className;
		frame.appendChild(playerFor(kind, id));
		button.replaceWith(frame);

		// A keyboard user who presses Enter on the button must not lose focus
		// to document.body once it is gone — the replacement is this section's
		// primary interaction, so it takes focus itself.
		frame.setAttribute('tabindex', '-1');
		frame.focus({ preventScroll: true });
	});

	// The silent background loop: reduced motion is structural, not a
	// best-effort pause after the fact. `preload: 'none'` and no `autoplay`
	// attribute on the <video> itself (see sections/miy-video.liquid) mean the
	// browser fetches nothing until this script explicitly arms it — so a
	// shopper who asks for reduced motion never causes a single loop byte to
	// move, rather than fetching the video and only then pausing it.
	var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

	function armLoops(root) {
		(root || document).querySelectorAll('[data-miy-video="loop"] video').forEach(function (v) {
			// `position: absolute` content paints after non-positioned in-flow
			// content regardless of DOM order or z-index: auto, so the poster
			// permanently covers the video unless something explicitly toggles
			// it. That something is this script, not stacking order or
			// `:has()` — it is the one place that already knows whether
			// playback has actually started.
			// Two selectors on purpose. miy-video names its fallback by class
			// and predates this contract; miy-hero-video and anything added
			// later opt in with the data attribute instead of adopting another
			// section's class name. Dropping the class would silently break
			// the loop path in sections/miy-video.liquid.
			var fallback = v
				.closest('[data-miy-video="loop"]')
				.querySelector('[data-miy-video-fallback], .miy-videosec__fallback');

			if (reducedMotion.matches) {
				v.pause();
				v.hidden = true;
				if (fallback) fallback.hidden = false;
				return;
			}

			v.hidden = false;
			if (v.dataset.miyArmed) return;
			v.dataset.miyArmed = '1';

			// Defer the bytes until the section is actually on screen.
			new IntersectionObserver(function (entries, observer) {
				entries.forEach(function (entry) {
					if (!entry.isIntersecting) return;
					observer.unobserve(entry.target);

					// Re-checked at fire time: the preference may have flipped
					// to reduced motion after this observer was armed but
					// before the section scrolled into view.
					if (reducedMotion.matches) return;

					// Listen for the video to genuinely start playing before hiding
					// the poster. If play() is rejected (e.g., autoplay blocked),
					// the listener never fires and the poster remains visible,
					// preventing a blank gap between the section scrolling into view
					// and video frames appearing. This is critical for hero sections
					// fed multi-megabyte files on slow connections.
					if (fallback) {
						entry.target.addEventListener('playing', function hideOnPlay() {
							entry.target.removeEventListener('playing', hideOnPlay);
							fallback.hidden = true;
						});
					}

					entry.target.play().catch(function () {});
				});
			}, { rootMargin: '200px' }).observe(v);
		});
	}

	armLoops();

	// An OS-level toggle mid-session is caught, not just the state at load.
	reducedMotion.addEventListener('change', function () {
		armLoops();
	});

	// A section the theme editor injects later (a merchant adding the
	// section, or reordering it) is armed too, not just what existed at load.
	document.addEventListener('shopify:section:load', function (event) {
		armLoops(event.target);
	});

	// Carousel arrows.
	//
	// WHY scrollIntoView({ inline }) INSTEAD OF scrollBy({ left })
	// scrollBy's `left` offset is physical, not logical: under dir="rtl" the
	// negative-scrolling convention this browser uses keeps scrollLeft at 0
	// at the track's own inline-start edge and goes negative as the shopper
	// advances, so a positive `left` (this handler's previous "next" step)
	// actually walked the track toward the inline start on /ar — both arrows
	// ran backwards. scrollIntoView's `inline: 'start'` is resolved against
	// the element's writing direction by the browser itself, so there is no
	// scrollLeft sign convention to get right and no dir check needed for
	// the movement itself.
	document.addEventListener('click', function (event) {
		var arrow = event.target.closest('[data-miy-carousel-arrow]');
		if (!arrow) return;

		var root = arrow.closest('[data-miy-carousel]');
		if (!root) return;

		var track = root.querySelector('[data-miy-carousel-track]');
		if (!track) return;

		var slides = Array.prototype.slice.call(track.children);
		if (!slides.length) return;

		var dir = arrow.getAttribute('data-miy-carousel-arrow') === 'prev' ? -1 : 1;

		// The inline-start edge is the left edge in English and the right
		// edge in Arabic. getBoundingClientRect is physical, so the computed
		// direction is asked which physical edge to measure from — and
		// nothing else. A first pass compared physical `left` unconditionally,
		// which in Arabic found the LAST visible slide instead of the first
		// (the track's inline-start is its right edge there), so "next"
		// jumped a full screenful instead of one slide. The movement itself
		// stays logical, via scrollIntoView below — this only fixes which
		// slide counts as "current".
		var rtl = window.getComputedStyle(track).direction === 'rtl';
		var trackRect = track.getBoundingClientRect();
		var trackStart = rtl ? trackRect.right : trackRect.left;

		var current = 0;
		var best = Infinity;
		slides.forEach(function (slide, i) {
			var rect = slide.getBoundingClientRect();
			var slideStart = rtl ? rect.right : rect.left;
			var distance = Math.abs(slideStart - trackStart);
			if (distance < best) {
				best = distance;
				current = i;
			}
		});

		var next = Math.min(slides.length - 1, Math.max(0, current + dir));
		slides[next].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
	});

	// The arrows advertise an action only this script can perform, so they
	// start `hidden` in the markup and are revealed once the script is
	// actually running — a shopper with the script blocked or failed still
	// gets the track itself, a plain scrollable list, with no dead buttons
	// sitting on top of it.
	function revealCarouselArrows(root) {
		(root || document).querySelectorAll('[data-miy-carousel]').forEach(function (carousel) {
			carousel.querySelectorAll('[data-miy-carousel-arrow]').forEach(function (arrow) {
				arrow.hidden = false;
			});
		});
	}

	revealCarouselArrows();

	// A carousel the theme editor injects later is revealed too, not just
	// what existed at load — same reasoning as armLoops above.
	document.addEventListener('shopify:section:load', function (event) {
		revealCarouselArrows(event.target);
	});

	// The photo mosaic rotation.
	//
	// WHY FRAMES AND NOT src SWAPPING
	// Reassigning src on four <img> tags is fewer DOM nodes, but the browser
	// then has to fetch and decode before it can paint, so every turn of the
	// rotation risks a blank tile — the exact failure this store already
	// shipped once and spent a session tracing (see §5 P0-2 in CLAUDE.md).
	// The frames are all in the document from the start, decoded, sharing one
	// URL per photograph, so a turn costs nothing but an opacity change.
	//
	// WHAT THIS SCRIPT DOES NOT DO
	// It does not decide the arrangement. sections/miy-mosaic.liquid shifts
	// each tile's frame list by that tile's own position, so moving every
	// tile to the same frame index is already a rotation with no photograph
	// repeated. All that is left here is when to advance.
	var MOSAIC_STAGGER_MS = 120;
	var MOSAIC_FALLBACK_DELAY_MS = 5000;

	var mosaicState = new WeakMap();

	function mosaicStateFor(mosaic) {
		var state = mosaicState.get(mosaic);
		if (!state) {
			state = { step: 0, timer: null, tiles: [], visible: false, held: false, bound: false };
			mosaicState.set(mosaic, state);
		}
		return state;
	}

	// How long the incoming frame takes to fade in, read back off the CSS so
	// the duration is stated in exactly one place. See the `.is-active` rule
	// in assets/custom-overrides.css.
	function mosaicFadeMs(tile) {
		var frame = tile.querySelector('.miy-mosaic__frame.is-active') || tile.querySelector('[data-miy-mosaic-frame]');
		if (!frame) return 0;

		var value = window.getComputedStyle(frame).transitionDuration.split(',')[0].trim();
		var ms = parseFloat(value) * (value.indexOf('ms') > -1 ? 1 : 1000);
		return isFinite(ms) && ms > 0 ? ms : 0;
	}

	// WHY THE HIDING HAPPENS HERE AND NOT IN THE STYLESHEET
	// custom-overrides.css is held to an invariant (tools/motion-guard/
	// css-invariants.test.js) that no rule in it may hide content unless the
	// reveal engine's own classes are in the selector — so that a page whose
	// JavaScript never runs can never have invisible content. The rotation
	// honours that by owning its hidden state outright: the stylesheet only
	// stacks the frames and orders them by z-index, and the opacity that
	// actually hides fifteen of the sixteen is set from here. With this script
	// dead, every frame stays opaque and the one the Liquid marked `is-active`
	// sits on top, so each tile still shows the right photograph.
	function setMosaicFrame(tile, index, fadeMs) {
		var frames = tile.querySelectorAll('[data-miy-mosaic-frame]');
		if (!frames.length) return;

		// The step counter runs negative, so that the picture in each tile
		// moves FORWARD to the next tile rather than backward. JavaScript's %
		// keeps the sign of its left operand, which would index off the front
		// of the list, hence the second modulo.
		var next = ((index % frames.length) + frames.length) % frames.length;

		frames.forEach(function (frame, i) {
			var active = i === next;
			var wasActive = frame.classList.contains('is-active');
			frame.classList.toggle('is-active', active);

			// Every photograph exists once per tile, so the frames that are
			// not showing are duplicates of what a screen reader has already
			// been given. Making them transparent hides them from sight and
			// from nobody else: they would still be announced, and any link
			// inside one would still take a tab stop. The markup ships with
			// these attributes already correct for step 0 — this only keeps
			// them correct as the rotation advances.
			if (active) frame.removeAttribute('aria-hidden');
			else frame.setAttribute('aria-hidden', 'true');

			frame.querySelectorAll('a[href]').forEach(function (link) {
				if (active) link.removeAttribute('tabindex');
				else link.setAttribute('tabindex', '-1');
			});

			if (active) {
				// `is-active` carries the transition, and the class and the
				// value land in the same style recalc, so the browser reads
				// the duration off the after-change style and animates the
				// fade in. An inactive frame declares no transition at all,
				// which is what makes every drop below a snap.
				frame.style.opacity = '1';
				return;
			}

			if (!wasActive || !fadeMs) {
				frame.style.opacity = '0';
				return;
			}

			// The frame that just handed its photograph on keeps it at full
			// opacity, now beneath the incoming one, until the fade-in has
			// completely covered it. Snapping it away immediately instead
			// would show the page through the half-faded frame above it.
			setTimeout(function () {
				frame.style.opacity = '0';
			}, fadeMs);
		});
	}

	function startMosaic(mosaic) {
		var state = mosaicStateFor(mosaic);
		if (state.timer) return;

		var delay = parseInt(mosaic.getAttribute('data-miy-mosaic-delay'), 10) || MOSAIC_FALLBACK_DELAY_MS;

		state.timer = setInterval(function () {
			state.step -= 1;

			// The tiles do not turn in unison. A simultaneous flip reads as
			// the whole section blinking; a short stagger reads as one move
			// travelling across the mosaic, which is what it is.
			state.tiles.forEach(function (tile, i) {
				setTimeout(function () {
					setMosaicFrame(tile, state.step, state.fadeMs);
				}, i * MOSAIC_STAGGER_MS);
			});
		}, delay);
	}

	function stopMosaic(mosaic) {
		var state = mosaicState.get(mosaic);
		if (!state || !state.timer) return;

		clearInterval(state.timer);
		state.timer = null;

		// The stagger timeouts already in flight are deliberately left to
		// finish. Cancelling them would strand the mosaic part-turned, with
		// one photograph visible in two tiles at once — a pause has to settle
		// on a complete arrangement, and the longest wait for that is the
		// stagger across the last tile, a few hundred milliseconds.
		// `state.step` survives, so resuming carries on instead of snapping
		// back to the merchant's original order.
	}

	// One gate, four inputs. Off-screen, a background tab, a pointer resting
	// on the section and the motion preference each have their own event
	// source; asking every one of them to call start or stop directly is how
	// a section ends up running because the last listener to fire happened to
	// be the permissive one.
	function syncMosaic(mosaic) {
		var state = mosaicStateFor(mosaic);
		var shouldRun = state.visible && !document.hidden && !state.held && !reducedMotion.matches;

		if (shouldRun) startMosaic(mosaic);
		else stopMosaic(mosaic);
	}

	function initMosaics(root) {
		(root || document).querySelectorAll('[data-miy-mosaic]').forEach(function (mosaic) {
			var state = mosaicStateFor(mosaic);

			if (!state.bound) {
				state.bound = true;
				state.tiles = Array.prototype.slice.call(mosaic.querySelectorAll('[data-miy-mosaic-tile]'));
				state.fadeMs = state.tiles.length ? mosaicFadeMs(state.tiles[0]) : 0;

				// Whether the cursor is allowed to hold the rotation at all.
				// Read from the markup rather than assumed, because the answer
				// is the merchant's: a tile is a link only when its block has
				// a Link set, and on this store none of them do.
				state.hasLinks = !!mosaic.querySelector('[data-miy-mosaic-tile] a[href]');

				// Take ownership of the hidden state before any rotation: the
				// frames arrive from Liquid all opaque, stacked, with the
				// right one on top. Passing 0 for the fade makes this a snap,
				// and since the active frame already covers its siblings the
				// shopper sees nothing change.
				state.tiles.forEach(function (tile) {
					setMosaicFrame(tile, 0, 0);
				});

				new IntersectionObserver(function (entries) {
					entries.forEach(function (entry) {
						state.visible = entry.isIntersecting;
						syncMosaic(mosaic);
					});
				}).observe(mosaic);

				// A shopper reaching for a photograph that links somewhere
				// must not have it change under the cursor mid-reach. Mouse
				// only, and checked at the event rather than by media query:
				// a tap fires pointerenter too, and on a touch screen the
				// matching pointerleave may never come, which would leave the
				// rotation held for good after a single stray tap.
				//
				// AND ONLY WHEN THERE IS SOMETHING TO REACH FOR. The sentence
				// above is conditional and the code was not: it held whether
				// or not any photograph linked anywhere. With no links — this
				// store's actual configuration — that pauses a band roughly
				// 1410x376, near enough the full width of the page, and the
				// cursor rests there constantly. Worse, it is exactly where
				// the cursor sits while someone watches to see whether the
				// pictures change, so the rotation reliably stopped for the
				// one person looking at it. Measured before the fix: 16s with
				// the cursor away gave three turns; 13s with it resting on the
				// section gave none.
				//
				// The protection re-arms by itself the moment a merchant fills
				// in a Link on any tile.
				mosaic.addEventListener('pointerenter', function (event) {
					if (!state.hasLinks) return;
					if (event.pointerType && event.pointerType !== 'mouse') return;
					state.held = true;
					syncMosaic(mosaic);
				});

				mosaic.addEventListener('pointerleave', function () {
					state.held = false;
					syncMosaic(mosaic);
				});

				// Keyboard focus counts as reaching for it too.
				mosaic.addEventListener('focusin', function () {
					state.held = true;
					syncMosaic(mosaic);
				});

				mosaic.addEventListener('focusout', function () {
					state.held = false;
					syncMosaic(mosaic);
				});
			}

			syncMosaic(mosaic);
		});
	}

	initMosaics();

	// Nothing turns in a tab the shopper is not looking at.
	document.addEventListener('visibilitychange', function () {
		initMosaics();
	});

	// Both of these re-run the gate rather than the setup: `state.bound`
	// makes a second pass over an already-wired mosaic free, and a section
	// the theme editor replaces arrives as a genuinely new element with its
	// own state, so there is nothing to tear down.
	reducedMotion.addEventListener('change', function () {
		initMosaics();
	});

	document.addEventListener('shopify:section:load', function (event) {
		initMosaics(event.target);
	});
})();
